import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search } from 'lucide-react';
import { BACKEND_URL } from '../config';
import SEO from '../components/SEO';
import LeadOffer from '../components/site/LeadOffer';
import { SitePage, PageHero, PackageCard, CardSkeletons, CtaBand } from '../components/site/ui';
import { toNumber } from '../components/site/brand';
import { DESTINATIONS } from '../components/site/destinations';

const regionOf = (dest) => {
  const d = DESTINATIONS[(dest || '').toLowerCase()];
  return d ? (d.region === 'India' ? 'domestic' : 'international') : 'international';
};
const labelOf = (dest) => DESTINATIONS[(dest || '').toLowerCase()]?.name || dest;

const AllPackages = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const query = params.get('search') || '';
  const type = params.get('type') || 'all';
  const dest = params.get('destination') || 'all';
  const sort = params.get('sort') || 'popular';

  const update = (key, value, fallback) => {
    const next = new URLSearchParams(params);
    if (!value || value === fallback) next.delete(key); else next.set(key, value);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/packages`)
      .then((r) => setPackages(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const destOptions = useMemo(() => {
    const set = new Map();
    packages.forEach((p) => {
      const k = (p.destination || '').toLowerCase();
      if (!k) return;
      if (type !== 'all' && regionOf(k) !== type) return;
      set.set(k, labelOf(k));
    });
    return [...set.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [packages, type]);

  const list = useMemo(() => {
    const q = query.trim().toLowerCase();
    let out = packages.filter((p) => {
      const k = (p.destination || '').toLowerCase();
      if (type !== 'all' && regionOf(k) !== type) return false;
      if (dest !== 'all' && k !== dest) return false;
      if (q) {
        const hay = [p.title, p.destination, labelOf(k), ...(Array.isArray(p.highlights) ? p.highlights : [])].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const price = (p) => toNumber(p.price) || Number.MAX_SAFE_INTEGER;
    if (sort === 'price-asc') out = [...out].sort((a, b) => price(a) - price(b));
    else if (sort === 'price-desc') out = [...out].sort((a, b) => (toNumber(b.price) || 0) - (toNumber(a.price) || 0));
    else out = [...out].sort((a, b) => ((b.bestSeller ? 1 : 0) - (a.bestSeller ? 1 : 0)) || ((Number(b.rating) || 0) - (Number(a.rating) || 0)));
    return out;
  }, [packages, query, type, dest, sort]);

  return (
    <SitePage>
      <SEO title="All Tour Packages · India & International" description="Browse every VK Tours holiday package — India and international destinations with transparent per-person prices." path="/tour-packages" />
      <PageHero
        short
        crumbs={[{ label: 'Home', to: '/' }, { label: 'All packages' }]}
        kicker="Ready-to-book holidays"
        title="All"
        accent="packages"
        lede="Every package can be customised — hotels, flights and pace — to suit your dates and budget."
      />

      <section className="vk-section vk-section--sand" style={{ paddingTop: 32 }}>
        <div className="vk-wrap">
          <div className="vk-filterbar" role="search">
            <label className="vk-filterbar__search">
              <Search size={18} aria-hidden="true" />
              <input type="search" placeholder="Search Bali, honeymoon, Kedarnath…" value={query} onChange={(e) => update('search', e.target.value, '')} aria-label="Search packages" />
            </label>
            <div className="vk-pills" role="tablist" aria-label="Region">
              {[['all', 'All'], ['international', 'International'], ['domestic', 'India']].map(([v, l]) => (
                <button key={v} type="button" role="tab" aria-selected={type === v} className={`vk-pill ${type === v ? 'is-active' : ''}`} onClick={() => { const n = new URLSearchParams(params); if (v === 'all') n.delete('type'); else n.set('type', v); n.delete('destination'); setParams(n, { replace: true }); }}>{l}</button>
              ))}
            </div>
            <div className="vk-filterbar__selects">
              <select className="vk-select" value={dest} onChange={(e) => update('destination', e.target.value, 'all')} aria-label="Destination">
                <option value="all">All destinations</option>
                {destOptions.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
              <select className="vk-select" value={sort} onChange={(e) => update('sort', e.target.value, 'popular')} aria-label="Sort">
                <option value="popular">Most popular</option>
                <option value="price-asc">Price: low to high</option>
                <option value="price-desc">Price: high to low</option>
              </select>
            </div>
          </div>

          <p className="vk-form__note" style={{ margin: '18px 0' }} aria-live="polite">
            {loading ? 'Loading packages…' : `Showing ${list.length} of ${packages.length} packages`}
          </p>

          {loading ? <CardSkeletons count={8} /> : error ? (
            <div className="vk-empty"><h3>Couldn’t load packages</h3><p>Please refresh the page, or WhatsApp us and we’ll send options directly.</p></div>
          ) : list.length ? (
            <div className="vk-grid">
              {list.map((p) => <PackageCard key={p.id} pkg={p} placeLabel={labelOf(p.destination)} onOpen={() => navigate(`/package/${p.id}`)} />)}
            </div>
          ) : (
            <div className="vk-empty">
              <h3>No packages match</h3>
              <p>Try a different search, or tell us where you want to go — we build custom trips every day.</p>
              <button type="button" className="vk-btn vk-btn--dark" onClick={() => setParams(new URLSearchParams(), { replace: true })}>Clear filters</button>
            </div>
          )}
        </div>
      </section>

      <LeadOffer pageLabel="all tour packages" />

      <CtaBand />
    </SitePage>
  );
};

export default AllPackages;
